import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { ApiService } from 'src/app/core/services/api.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { BelenderWidgetResponse } from 'src/app/models/BelenderWidgetResponse';
import { CancelRequest } from 'src/app/models/CancelRequest';
import { CancelResponse } from 'src/app/models/CancelResponse';
import { ODDetailResponse } from 'src/app/models/OpenData.ODDetailResponse';
import { PersonQueryStatusResponse } from 'src/app/models/PersonQueryStatusResponse';
import { ENRICHMENT_RULES_TO_REMOVE } from 'src/app/shared/constants/card-documents.constants';
import { UtilsService } from 'src/app/utils/utils.service';
import { CardRequestDetailDocumentsService } from './card-request-detail-documents/card-request-detail-documents.service';


export type EnrichmentDecision = 'Viable' | 'No viable' | 'No evaluable';


@Injectable({
    providedIn: 'root'
})
export class CardRequestDetailService {

    isLoading = false;
    request: ODDetailResponse;
    queryId: string;
    lastQueryHour: Date;
    isHistorySidebarVisible = false;
    isCalculateViabilitySidebarVisible = false;
    feasibilityEnrichmentData: Record<string, any> | undefined = undefined;

    constructor(
		private readonly apiService: ApiService,
		private readonly router: Router,
		private readonly toastService: ToastService,
        private readonly cardRequestDetailDocumentsService: CardRequestDetailDocumentsService,
        private readonly utilsService: UtilsService,
        private readonly requestDetailDocumentsService: CardRequestDetailDocumentsService
    ) { }

    getBelenderWidget(param1: string, param2: string, param3: string): Promise<BelenderWidgetResponse> {
        return this.apiService.get(`requests/belender-widget?param1=${param1}&param2=${param2}&param3=${param3}`);
    }

    getRequestFullPersonName(): string {
        if (this.request != null) {
            return this.request.personQuery?.firstName + ' ' +
				this.request.personQuery?.firstSurname + ' ' +
				(this.request.personQuery.secondSurname ? this.request.personQuery.secondSurname : '');
        }
        return null;
    }

    async saveDocument(url: string, fileName: string): Promise<void> {
        const file = await this.apiService.postBlob(`files/amazon-file`, { url: url, fileName: fileName });
        saveAs(file, fileName);
    }

    saveJSON(json: object, fileName: string): void {
        // Convert the object to a JSON string
        const jsonString = JSON.stringify(json);
        // Create a Blob from the JSON string
        const blob = new Blob([jsonString], { type: 'application/json' });
        saveAs(blob, fileName);
    }

    async getRequestFromUrl(withoutGuard = false): Promise<void> {
        this.queryId = this.validateURLAndGetRequestId(this.router.url);
        if (this.queryId) {
            await this.refreshRequest(withoutGuard);
        } else {
            this.toastService.showError('TOAST.ERROR', 'REQUEST_DETAIL.NO_QUERY_ID', true);
        };
    }

    async refreshRequest(withoutGuard: boolean): Promise<void> {
        try {
            this.cardRequestDetailDocumentsService.cleanFeasibilityEnrichmentData();
            this.isLoading = true;

            await this.setRequest(withoutGuard);
            await this.setConsentUrlBoxed();
            this.cardRequestDetailDocumentsService.setDocumentNames(this.request);
            this.cardRequestDetailDocumentsService.expandAll();
        } catch {
            this.toastService.showError('TOAST.ERROR', 'REQUEST_DETAIL.ERROR_NOT_FOUND', true);
        } finally {
            this.isLoading = false;
        }
    }

    private async setConsentUrlBoxed(): Promise<void> {
        if (!this.request.personQuery.consentUrlBoxed) {
            const req = { queryId: this.queryId };
            const res = await this.apiService.post<PersonQueryStatusResponse>('cards/status', req);
            this.request.personQuery.consentUrlBoxed = res.consentUrlBoxed ? res.consentUrlBoxed : this.utilsService.convertConsentUrlToConsentUrlBoxed(res.consentUrl);
        }
    }

    cancelRequest(cancelRequest: CancelRequest): Promise<CancelResponse> {
        return this.apiService.post<CancelResponse>('cards/cancel', cancelRequest);
    }

    private validateURLAndGetRequestId(url: string): string | null {
        const regex = /request-detail\/([0-9]{14}-[A-Z0-9]{18})/;
        const matches = url.match(regex);
        if (matches && matches[1]) {
            return matches[1];
        } else {
            return null;
        }
    }

    private async setRequest(withoutGuard: boolean): Promise<void> {
        if (withoutGuard){
            this.request = await this.apiService.get<ODDetailResponse>(`cards?queryId=${this.queryId}`);
        }
        this.lastQueryHour = new Date();
        this.setFeasibilityData();
    }

    private setFeasibilityData(): void {
        const enrichment = this.request.data?.['Enrichment'];
        if (!enrichment) return;
        this.setDecision(enrichment);
        this.setReglasDenegacion(enrichment);
    }

    private setDecision(enrichment: Record<string,any>): void {
        const decision = enrichment?.['Decision'];
        if (!decision) return;

        this.feasibilityEnrichmentData = { Decisión: decision?.['Decision'] };

        if (decision?.['Decision'] !== 'No evaluable') {
            this.feasibilityEnrichmentData['Limite'] = decision?.['Limite'] / 100;
        }

        this.requestDetailDocumentsService.addFeasibilityEnrichmentDataToAccordion('Enrichment: Decisión', this.feasibilityEnrichmentData || {});
    }

    private setReglasDenegacion(enrichment: Record<string,any>): void {
        this.feasibilityEnrichmentData = {};
        const decision = enrichment?.['Decision'];
        if (!decision) return;

        const reglasDenegacion = decision?.['ReglasDecision'];
        if (!reglasDenegacion || reglasDenegacion.length === 0) return;

        const rulesToShow = decision?.['Decision'] === 'No evaluable'
            ? reglasDenegacion.filter((regla: any) => regla.Codigo === 'OD999')
            : reglasDenegacion.filter((regla: any) => regla.resultado === 'KO' && !ENRICHMENT_RULES_TO_REMOVE.includes(regla.Codigo));

        if (rulesToShow.length < 1) return;

        rulesToShow.forEach((regla: any) => {
            const result = regla.resultado;
            const code = regla.Codigo;
            const value = regla.Descripcion;
            const key = `${code}: ${value}`;
            this.feasibilityEnrichmentData[key] = result;
        });

        this.requestDetailDocumentsService.addFeasibilityEnrichmentDataToAccordion('Enrichment: Reglas denegación', this.feasibilityEnrichmentData || {});
    }

    private setScoreDelphi(enrichment: Record<string,any>): void {
        const scoreDeplhi = enrichment?.['ScoreDelphi'];

        this.feasibilityEnrichmentData = {
            scoreD4A: scoreDeplhi?.['ScoreD4A'],
            pdD4A: scoreDeplhi?.['PdD4A']
        };

        this.requestDetailDocumentsService.addFeasibilityEnrichmentDataToAccordion('Enrichment: Score Delphi', this.feasibilityEnrichmentData || {});
    }

    resetFeasibilityData(): void {
        this.feasibilityEnrichmentData = undefined;
    }

    isDecisionAvailable(): boolean {
        const enrichment = this.request.data?.['Enrichment'];
        const decision = enrichment?.['Decision'];
        return decision ? true : false;
    }

    isDecision(value: EnrichmentDecision): boolean {
        const enrichment = this.request.data?.['Enrichment'];
        const decision = enrichment?.['Decision'];
        return decision?.Decision === value;
    }
}
